import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import * as React from "react";
import { DayPicker } from "react-day-picker";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({
	className,
	classNames,
	showOutsideDays = true,
	...props
}: CalendarProps) {
	return (
		<DayPicker
			showOutsideDays={showOutsideDays}
			className={cn("p-3", className)}
			classNames={{
				months: "relative flex flex-col sm:flex-row gap-2",
				month: "flex flex-col gap-4",
				month_caption: "flex justify-center pt-1 items-center",
				caption_label: "text-sm font-medium",
				nav: "absolute inset-x-0 top-0 flex items-center justify-between px-1",
				button_previous: cn(
					buttonVariants({ variant: "outline" }),
					"size-7 bg-transparent p-0 opacity-60 hover:opacity-100",
				),
				button_next: cn(
					buttonVariants({ variant: "outline" }),
					"size-7 bg-transparent p-0 opacity-60 hover:opacity-100",
				),
				month_grid: "w-full border-collapse space-y-1",
				weekdays: "flex",
				weekday:
					"text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
				week: "flex w-full mt-2",
				day: cn(
					"relative p-0 text-center text-sm focus-within:relative focus-within:z-20",
					props.mode === "range"
						? "[&:has([aria-selected].day-range-start)]:rounded-l-md [&:has([aria-selected].day-range-end)]:rounded-r-md"
						: "",
				),
				day_button: cn(
					buttonVariants({ variant: "ghost" }),
					"size-9 rounded-md p-0 font-normal",
					"hover:bg-muted hover:text-foreground",
				),
				range_start: "day-range-start rounded-l-md",
				range_end: "day-range-end rounded-r-md",
				selected: cn(
					"[&>button]:bg-primary [&>button]:text-primary-foreground",
					"[&>button]:shadow-[inset_0_1px_0_rgba(255,255,255,0.25)]",
					"[&>button]:hover:bg-primary [&>button]:hover:text-primary-foreground",
					"[&>button]:focus-visible:bg-primary [&>button]:focus-visible:text-primary-foreground",
				),
				today:
					"[&>button]:font-semibold [&:not([data-selected=true])>button]:ring-1 [&:not([data-selected=true])>button]:ring-primary [&:not([data-selected=true])>button]:ring-inset",
				outside:
					"day-outside [&>button]:text-muted-foreground [&>button]:opacity-50",
				disabled:
					"[&>button]:text-muted-foreground [&>button]:opacity-40 [&>button]:pointer-events-none [&>button]:hover:bg-transparent [&>button]:hover:text-muted-foreground",
				range_middle:
					"rounded-none [&>button]:bg-muted [&>button]:text-foreground [&>button]:hover:bg-muted",
				hidden: "invisible",
				...classNames,
			}}
			components={{
				Chevron: ({ orientation }) =>
					orientation === "left" ? (
						<CaretLeft aria-hidden="true" className="size-4" />
					) : (
						<CaretRight aria-hidden="true" className="size-4" />
					),
			}}
			{...props}
		/>
	);
}

Calendar.displayName = "Calendar";

export { Calendar };
